import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  useTheme,
  alpha,
  Stack,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";

// Icons for better UX
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SubjectIcon from "@mui/icons-material/Subject";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CategoryIcon from "@mui/icons-material/Category";
import InfoIcon from "@mui/icons-material/Info";
import EventIcon from "@mui/icons-material/Event";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";

const SearchForm = ({ onSearchChange }) => {
  const theme = useTheme();

  // Helper function to get default dates (30 minutes difference from now)
  const getDefaultDates = () => {
    const now = new Date();
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

    // Format to datetime-local format (YYYY-MM-DDTHH:mm)
    const formatToLocalDateTime = (date) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    return {
      fromDate: formatToLocalDateTime(thirtyMinutesAgo),
      toDate: formatToLocalDateTime(now),
    };
  };

  const defaultDates = getDefaultDates();

  // State
  const [subject, setSubject] = useState("");
  const [euid, setEuid] = useState("");
  const [logStatus, setLogStatus] = useState("");
  const [logType, setLogType] = useState("");
  const [fromEmailId, setFromEmailId] = useState("");
  const [toEmailIds, setToEmailIds] = useState("");
  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);

  // Validation errors
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Validate From Email
    if (fromEmailId && !isValidEmail(fromEmailId)) {
      newErrors.fromEmailId = "Invalid email format";
    }

    // Validate To Emails (comma separated)
    if (toEmailIds) {
      const emails = toEmailIds
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);
      const invalidEmails = emails.filter((email) => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        newErrors.toEmailIds = `Invalid email(s): ${invalidEmails.join(", ")}`;
      }
    }

    // Validate EUID format (should be alphanumeric, allow common special chars)
    if (euid && !/^[a-zA-Z0-9\-_]+$/.test(euid)) {
      newErrors.euid =
        "EUID should contain only alphanumeric characters, hyphens, or underscores";
    }

    // Validate Date Range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      if (from > to) {
        newErrors.dateRange = '"From Date" cannot be after "To Date"';
      }

      // Check if range is not more than 90 days
      const daysDiff = (to - from) / (1000 * 60 * 60 * 24);
      if (daysDiff > 30) {
        newErrors.dateRange = "Date range cannot exceed 30 days";
      }
    }

    // Validate From Date is not in future
    if (fromDate) {
      const from = new Date(fromDate);
      if (from > new Date()) {
        newErrors.fromDate = '"From Date" cannot be in the future';
      }
    }

    // Validate To Date is not in future
    if (toDate) {
      const to = new Date(toDate);
      if (to > new Date()) {
        newErrors.toDate = '"To Date" cannot be in the future';
      }
    }

    return newErrors;
  };

  // Helper to format date for API
  const toLocalISOWithOffset = (value) => {
    if (!value) return undefined;
    const date = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");

    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? "+" : "-";
    const hours = pad(Math.floor(Math.abs(offset) / 60));
    const minutes = pad(Math.abs(offset) % 60);
    const tzOffset = `${sign}${hours}:${minutes}`;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}${tzOffset}`;
  };

  const handleSearchChange = () => {
    // Validate form
    const validationErrors = validateForm();
    setErrors(validationErrors);

    // If there are errors, show snackbar and don't proceed
    if (Object.keys(validationErrors).length > 0) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors above",
        severity: "error",
      });
      return;
    }

    const searchData = {
      subject: subject || undefined,
      euid: euid || undefined,
      from_email_id: fromEmailId || undefined,
      log_status: logStatus || undefined,
      log_type: logType || undefined,
      to_email_ids: toEmailIds
        ? toEmailIds
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean)
        : undefined,
      date_range: {
        from_date: toLocalISOWithOffset(fromDate),
        to_date: toLocalISOWithOffset(toDate),
      },
    };

    // Clean up undefined or empty objects
    Object.keys(searchData).forEach((key) => {
      if (
        searchData[key] === undefined ||
        (typeof searchData[key] === "object" &&
          Object.values(searchData[key]).every((v) => !v))
      ) {
        delete searchData[key];
      }
    });

    console.log("Search Data:", searchData);
    onSearchChange(searchData);

    setSnackbar({
      open: true,
      message: "Search executed successfully",
      severity: "success",
    });
  };

  const handleReset = () => {
    setSubject("");
    setEuid("");
    setLogStatus("");
    setLogType("");
    setFromEmailId("");
    setToEmailIds("");
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setErrors({});
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 900,
          padding: 4,
          borderRadius: 4,
          backdropFilter: "blur(12px)",
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: theme.shadows[4],
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <FilterListIcon color="primary" />
          </Box>
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            Filter Logs
          </Typography>
        </Stack>

        {/* Global Error Message */}
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="600">
              ⚠️ Please fix the following errors:
            </Typography>
            {errors.dateRange && (
              <Typography variant="caption" display="block">
                • {errors.dateRange}
              </Typography>
            )}
            {errors.fromDate && (
              <Typography variant="caption" display="block">
                • {errors.fromDate}
              </Typography>
            )}
            {errors.toDate && (
              <Typography variant="caption" display="block">
                • {errors.toDate}
              </Typography>
            )}
            {errors.fromEmailId && (
              <Typography variant="caption" display="block">
                • From Email: {errors.fromEmailId}
              </Typography>
            )}
            {errors.toEmailIds && (
              <Typography variant="caption" display="block">
                • To Emails: {errors.toEmailIds}
              </Typography>
            )}
            {errors.euid && (
              <Typography variant="caption" display="block">
                • EUID: {errors.euid}
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Row 1: Core Identity */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Subject"
              variant="outlined"
              fullWidth
              size="small"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SubjectIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="EUID"
              variant="outlined"
              fullWidth
              size="small"
              value={euid}
              onChange={(e) => setEuid(e.target.value)}
              error={!!errors.euid}
              helperText={errors.euid}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FingerprintIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Row 2: Emails */}
          <Grid item xs={12} md={6}>
            <TextField
              label="From Email ID"
              variant="outlined"
              type="email"
              fullWidth
              size="small"
              value={fromEmailId}
              onChange={(e) => setFromEmailId(e.target.value)}
              error={!!errors.fromEmailId}
              helperText={errors.fromEmailId}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AlternateEmailIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="To Email IDs (comma separated)"
              variant="outlined"
              fullWidth
              size="small"
              value={toEmailIds}
              onChange={(e) => setToEmailIds(e.target.value)}
              error={!!errors.toEmailIds}
              helperText={
                errors.toEmailIds || "Separate multiple emails with commas"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PeopleOutlineIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Row 3: Status & Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Log Status"
              variant="outlined"
              fullWidth
              size="small"
              value={logStatus}
              onChange={(e) => setLogStatus(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InfoIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Log Type"
              variant="outlined"
              fullWidth
              size="small"
              value={logType}
              onChange={(e) => setLogType(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider
              textAlign="left"
              sx={{ mb: 2, mt: 1, color: "text.secondary", fontSize: "0.8rem" }}
            >
              TIME RANGE
            </Divider>
          </Grid>

          {/* Row 4: Dates */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="From Date"
              variant="outlined"
              type="datetime-local"
              fullWidth
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              error={!!errors.fromDate}
              helperText={errors.fromDate}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="To Date"
              variant="outlined"
              type="datetime-local"
              fullWidth
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              error={!!errors.toDate || !!errors.dateRange}
              helperText={errors.toDate || errors.dateRange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Row 5: Actions */}
          <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchChange}
              startIcon={<SearchIcon />}
              sx={{
                flex: 2,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "none"
                    : "0 4px 14px 0 rgba(0,118,255,0.39)",
              }}
            >
              Search Logs
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default SearchForm;